import React from 'react';

import CSSModules from 'react-css-modules';
import {useIntl} from 'react-intl';

import {Text} from '@edume/magnificent';

import {useSelector} from '../../../state/hooks';
import {getSelectedGroupId} from '../../../state/team/teamSelectors';
import {useGetVideoLibraryQuery} from '../../../state/videoLibrary/videoLibraryApi';
import type {Video} from '../../../state/videoLibrary/videoLibraryApi';
import {MediaLibrary} from './MediaLibrary';
import {useDeleteModal} from './useDeleteModal';
import {VideoLibraryPreview} from './VideoLibraryPreview';

import styles from './styles.module.scss';

interface VideoLibraryProps {
  startUpload: (files: File[]) => void;
  chooseVideo: (video: Pick<Video, 'id' | 'url' | 'filename'>) => void;
  deleteVideo: (id: number) => void;
  setIsDeleteModalOpen: (arg: boolean) => void;
  isGroupLibrary: boolean;
}

const VideoLibraryComponent = ({
  deleteVideo,
  chooseVideo,
  startUpload,
  setIsDeleteModalOpen,
  isGroupLibrary,
}: VideoLibraryProps) => {
  const selectedGroupId = useSelector(getSelectedGroupId);
  const {
    data: {externalVideos, groupVideos} = {externalVideos: [], groupVideos: []},
    isLoading: isLibraryLoading,
  } = useGetVideoLibraryQuery(selectedGroupId);
  const videosFromLibrary = isGroupLibrary ? groupVideos : externalVideos;
  const intl = useIntl();
  const description = isGroupLibrary
    ? intl.formatMessage({id: 'VideoLibrary.description.group'})
    : intl.formatMessage({id: 'VideoLibrary.description.external'});

  const {showDeleteModal, onDelete, onCancel, onConfirmDelete} = useDeleteModal(
    deleteVideo,
    setIsDeleteModalOpen
  );

  const renderVideos = videosFromLibrary.map((video) => (
    <VideoLibraryPreview
      key={video.mediaLibraryAssetId}
      video={video}
      chooseVideo={chooseVideo}
      onDelete={onDelete}
    />
  ));

  return (
    <MediaLibrary
      type='video'
      showDeleteModal={showDeleteModal}
      isLibraryLoading={isLibraryLoading}
      renderMedia={renderVideos}
      startUpload={startUpload}
      onCancel={onCancel}
      onConfirmDelete={onConfirmDelete}
      description={<Text size='m'>{description}</Text>}
      hasUploader={isGroupLibrary}
    />
  );
};

export const VideoLibrary = CSSModules(VideoLibraryComponent, styles);
